import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, CheckCircle, Phone, Mail, MapPin,
  Users, Briefcase, ArrowRight, MessageSquare
} from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Appointment.css';
import { AppointmentHero } from '../components/PageHeroes';
import { sendEmail, ENDPOINTS } from '../utils/sendEmail';

const Appointment = () => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    company: '', employees: '', consultationType: '',
    preferredDate: '', preferredTime: '', message: ''
  });
  const [status, setStatus] = useState('idle');

  const consultationTypes = [
    { icon: Briefcase,     title: 'Platform Setup',     description: "New to Effective Global Staff? We'll walk you through payroll, invoicing, accounting, and everything else from scratch.", accent: 'blue' },
    { icon: Users,         title: 'Switching Provider', description: 'Moving from another payroll, accounting, or invoicing platform — we handle the migration.',           accent: 'green' },
    { icon: MessageSquare, title: 'General Enquiry',    description: 'Have questions about pricing, compliance, or which features fit your business.',            accent: 'blue' },
    { icon: Phone,         title: 'Technical Support',  description: 'Existing client needing hands-on guidance or troubleshooting on any part of the platform.',      accent: 'green' }
  ];

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'
  ];

  const whyUs = [
    { icon: CheckCircle, text: 'UK-based product & compliance experts' },
    { icon: CheckCircle, text: 'No-obligation, completely free' },
    { icon: CheckCircle, text: 'HMRC & GDPR specialists' },
    { icon: CheckCircle, text: 'Response within 1 business hour' }
  ];

  const contactCards = [
    { icon: Phone, label: 'Call us directly', value: '+44 744 630 1591', href: 'tel:+447446301591' },
    { icon: Mail,  label: 'Email us', value: 'thomas@effectivestaff.co.uk', href: 'mailto:thomas@effectivestaff.co.uk' },
    { icon: MapPin, label: 'Our office', value: 'Flat 11, 3rd Floor, 777 Old Kent Road, London, SE15 1NZ', href: null }
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await sendEmail(ENDPOINTS.APPOINTMENT, {
        name:          `${form.firstName} ${form.lastName}`,
        firstName:         form.firstName,
        lastName:          form.lastName,
        email:              form.email,
        phone:              form.phone,
        company:            form.company,
        employees:          form.employees || 'Not specified',
        consultationType:  form.consultationType,
        preferredDate:     form.preferredDate,
        preferredTime:     form.preferredTime,
        message:            form.message || 'No additional notes.',
        to_email:           'thomas@effectivestaff.co.uk',
        reply_to:           form.email,
      });
      setStatus('success');
      setForm({
        firstName: '', lastName: '', email: '', phone: '',
        company: '', employees: '', consultationType: '',
        preferredDate: '', preferredTime: '', message: ''
      });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
  };
  const itemVariants = {
    hidden:  { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <motion.div className="appt-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <AppointmentHero />

      {/* ===== CONSULTATION TYPES ===== */}
      <section className="appt-section">
        <div className="appt-container">
          <motion.div className="appt-section-header" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <span className="appt-eyebrow-sm">Choose your session</span>
            <h2>What would you like to discuss?</h2>
            <p>Select the consultation type that best fits your situation - we will tailor the session accordingly.</p>
          </motion.div>
          <motion.div className="appt-types-grid" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {consultationTypes.map((type, idx) => {
              const Icon = type.icon;
              return (
                <motion.div key={idx} className={`appt-type-card accent-${type.accent}`} variants={itemVariants} whileHover={{ y: -6 }}>
                  <div className="appt-type-icon">
                    <Icon size={22} />
                  </div>
                  <h3>{type.title}</h3>
                  <p>{type.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== FORM + SIDEBAR ===== */}
      <section className="appt-section appt-section-alt">
        <div className="appt-container">
          <div className="appt-two-col">

            {/* Sidebar */}
            <motion.div className="appt-sidebar" initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="appt-eyebrow-sm">Why book with us</span>
              <h2>Expert advice, no strings attached</h2>
              <p className="appt-sidebar-intro">Our consultations are genuinely free. You will speak with a qualified specialist, not a salesperson.</p>

              <div className="appt-why-list">
                {whyUs.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="appt-why-item">
                      <Icon size={16} className="appt-why-icon" />
                      <span>{item.text}</span>
                    </div>
                  );
                })}
              </div>

              <p className="appt-sidebar-subhead">Other ways to reach us</p>
              <div className="appt-contact-cards">
                {contactCards.map((c, idx) => {
                  const Icon = c.icon;
                  const content = (
                    <>
                      <div className="appt-contact-icon">
                        <Icon size={17} />
                      </div>
                      <div>
                        <p className="appt-contact-label">{c.label}</p>
                        <p className="appt-contact-val">{c.value}</p>
                      </div>
                    </>
                  );
                  return c.href ? (
                    <a key={idx} href={c.href} className="appt-contact-item">{content}</a>
                  ) : (
                    <div key={idx} className="appt-contact-item appt-contact-item-static">{content}</div>
                  );
                })}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div className="appt-form-col" initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.08 }}>
              <div className="appt-form-card">
                <div className="appt-form-header">
                  <Calendar size={20} className="appt-form-header-icon" />
                  <h3>Book your consultation</h3>
                </div>

                {status === 'success' ? (
                  <div className="appt-success">
                    <CheckCircle size={44} className="appt-success-icon" />
                    <h3>Appointment requested!</h3>
                    <p>We will confirm your slot within 1 business hour. Check your email for details.</p>
                  </div>
                ) : (
                  <form className="appt-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>First name *</label>
                        <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="Jane" required />
                      </div>
                      <div className="form-group">
                        <label>Last name *</label>
                        <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Smith" required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Work email *</label>
                        <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="jane@company.co.uk" required />
                      </div>
                      <div className="form-group">
                        <label>Phone number *</label>
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="+44 7700 900000" required />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Company name *</label>
                        <input name="company" value={form.company} onChange={handleChange} placeholder="Acme Ltd" required />
                      </div>
                      <div className="form-group">
                        <label>Number of employees</label>
                        <select name="employees" value={form.employees} onChange={handleChange}>
                          <option value="">Select range</option>
                          <option value="1-10">1 - 10</option>
                          <option value="11-50">11 - 50</option>
                          <option value="51-200">51 - 200</option>
                          <option value="201-500">201 - 500</option>
                          <option value="500+">500+</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Consultation type *</label>
                      <select name="consultationType" value={form.consultationType} onChange={handleChange} required>
                        <option value="">Select type</option>
                        <option value="Platform Setup">Platform Setup</option>
                        <option value="Switching Provider">Switching Provider</option>
                        <option value="General Enquiry">General Enquiry</option>
                        <option value="Technical Support">Technical Support</option>
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Preferred date *</label>
                        <input type="date" name="preferredDate" value={form.preferredDate} onChange={handleChange} required min={new Date().toISOString().split('T')[0]} />
                      </div>
                      <div className="form-group">
                        <label>Preferred time *</label>
                        <select name="preferredTime" value={form.preferredTime} onChange={handleChange} required>
                          <option value="">Select time</option>
                          {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Anything you would like us to prepare for?</label>
                      <textarea name="message" value={form.message} onChange={handleChange} placeholder="e.g. We are migrating from Sage, have 45 employees, and need CIS support..." rows={3} />
                    </div>
                    {status === 'error' && (
                      <p className="form-error">Something went wrong. Please try again or call us directly.</p>
                    )}
                    <button type="submit" className="appt-submit-btn" disabled={status === 'sending'}>
                      {status === 'sending' ? 'Booking...' : (
                        <span className="appt-submit-content">
                          Confirm booking <ArrowRight size={15} />
                        </span>
                      )}
                    </button>
                    <p className="appt-form-note">Mon - Fri, 9am - 6pm. We will confirm within 1 business hour.</p>
                  </form>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Appointment;